import {
  authConnector,
  CHAIN_NAMESPACES,
  CONNECTOR_INITIAL_AUTHENTICATION_MODE,
  WALLET_CONNECTORS,
  Web3Auth,
  Web3AuthOptions,
} from "@web3auth/modal";
import { useCallback } from "react";
import { chainId, rpcTarget, web3authClientId, web3authNetwork } from "../utils/constants";
import { ethers } from "ethers";

const hiddenLoginMethods = [
  "google",
  "twitter",
  "facebook",
  "discord",
  "apple",
  "github",
  "reddit",
  "line",
  "kakao",
  "linkedin",
  "twitch",
  "wechat",
  "sms_passwordless",
] as const;

const useWeb3Auth = () => {
  const initialize = useCallback(async () => {
    const chainIdHex = ethers.toQuantity(chainId as string);
    const isPolygonMainnet = chainIdHex === "0x89";
    // The passwordless service rejects localhost URLs in the whitelabel logo ("Validation failed").
    const logoUrl =
      window.location.hostname === "localhost"
        ? "https://wallet.collecttrumpcards.com/DJT-portrait.png"
        : window.location.origin + "/DJT-portrait.png";

    const options: Web3AuthOptions = {
      clientId: web3authClientId as string,
      web3AuthNetwork: web3authNetwork,
      sessionTime: 3600,
      // The default "connect-and-sign" makes external wallets sign a SIWW message verified by Web3Auth's server,
      // which fails for MetaMask. We verify wallet ownership ourselves (VerifyUser), so connecting is enough.
      initialAuthenticationMode: CONNECTOR_INITIAL_AUTHENTICATION_MODE.CONNECT_ONLY,
      enableLogging: false,
      chains: [
        {
          chainNamespace: CHAIN_NAMESPACES.EIP155,
          chainId: chainIdHex,
          rpcTarget: rpcTarget!,
          displayName: isPolygonMainnet ? "Polygon" : "Polygon Amoy",
          blockExplorerUrl: isPolygonMainnet ? "https://polygonscan.com" : "https://amoy.polygonscan.com",
          ticker: "POL",
          tickerName: "Polygon",
          logo: "https://images.toruswallet.io/polygon.svg",
          isTestnet: !isPolygonMainnet,
        },
      ],
      defaultChainId: chainIdHex,
      // Keep the full URL (query string + hash) after the redirect login; the SDK default drops them.
      connectors: [authConnector({ connectorSettings: { redirectUrl: window.location.href } })],
      uiConfig: {
        mode: "light",
        logoLight: logoUrl,
        logoDark: logoUrl,
        appName: "Trump Digital Trading Cards",
        loginGridCol: 3,
        uxMode: "redirect",
        logoAlignment: "center",
        primaryButton: "emailLogin",
        signInMethods: ["passwordless", "externalWallets"],
        displayInstalledExternalWallets: false,
        buttonRadiusType: "pill",
        borderRadiusType: "large",
        hideSuccessScreen: true,
      },
      modalConfig: {
        connectors: {
          [WALLET_CONNECTORS.AUTH]: {
            label: "auth",
            loginMethods: {
              ...Object.fromEntries(hiddenLoginMethods.map((name) => [name, { name, showOnModal: false }])),
              email_passwordless: { name: "email_passwordless", showOnModal: true },
            },
          },
          [WALLET_CONNECTORS.WALLET_CONNECT_V2]: {
            label: "walletc",
            showOnModal: false,
          },
        },
      },
    };

    // DEBUG: log the Web3Auth project whitelist and whether the current origin is on it
    fetch(
      `https://signer.web3auth.io/api/configuration?project_id=${web3authClientId}&network=${web3authNetwork}&whitelist=true`
    )
      .then((res) => res.json())
      .then((config) => {
        const urls: string[] = config?.whitelist?.urls ?? [];
        console.log("[Web3Auth] network:", config?.network ?? config?.error);
        console.log("[Web3Auth] whitelisted origins:", urls);
        console.log(
          `[Web3Auth] current origin ${window.location.origin} whitelisted:`,
          urls.some((url) => url.replace(/\/$/, "") === window.location.origin)
        );
      })
      .catch((err) => console.error("[Web3Auth] config fetch failed:", err));

    const web3Auth = new Web3Auth(options);
    await web3Auth.init();
    return web3Auth;
  }, []);

  return { initialize };
};

export default useWeb3Auth;
